import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { EmptyState } from '@/components/EmptyState';
import { Bell, ExternalLink, MapPin, Clock, Bookmark, Building2, User as UserIcon, Home as HomeIcon, Briefcase, Truck } from 'lucide-react';
import { SaveToCollectionModal } from '@/components/SaveToCollectionModal';

/**
 * Alerts — the OPPORTUNITY FEED. Per user direction:
 *   "Users should open Alerts and see 'three refrigerators, two HVAC leads,
 *   one delivery job Ari found' — not a wall of 'scan complete' notifications."
 *
 * We render real listing cards sorted newest-first, with source, distance,
 * date, and per-card [View Listing] + [Save to Collection] actions. A tiny
 * "Scan complete · no matches" strip only appears when the feed is empty and
 * a recent scan produced no results (so we honestly explain why the feed is
 * quiet instead of showing a generic empty state).
 */

// ---------------------------------------------------------------------------
// Time helpers
// ---------------------------------------------------------------------------
const relTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.round((now - then) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
};

const priceLabel = (op) => {
  if (op.is_free) return 'FREE';
  if (op.opportunity_type === 'lead') {
    if (op.budget) return typeof op.budget === 'number' ? `$${op.budget}` : String(op.budget);
    return 'Lead';
  }
  if (typeof op.price === 'number') return `$${op.price.toLocaleString()}`;
  return null;
};

const opportunityHeader = (op) => {
  const t = op.opportunity_type;
  if (t === 'lead') return { badge: 'New Lead', Icon: Briefcase };
  if (t === 'business') return { badge: 'New Business Match', Icon: Building2 };
  if (t === 'candidate') return { badge: 'New Candidate', Icon: UserIcon };
  if (t === 'job') return { badge: 'New Job Opportunity', Icon: Briefcase };
  if (t === 'delivery') return { badge: 'New Delivery Route', Icon: Truck };
  return { badge: 'New Opportunity', Icon: HomeIcon };
};

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------
const OpportunityCard = ({ op, onSave }) => {
  const { badge, Icon } = opportunityHeader(op);
  const price = priceLabel(op);
  const thumb = op.photos?.[0];

  return (
    <div className="zita-card p-4 flex gap-4">
      {/* Thumbnail (or icon placeholder) */}
      <div
        className="shrink-0 w-24 h-24 sm:w-32 sm:h-32 rounded-xl overflow-hidden bg-[hsl(var(--zita-silver-soft))] flex items-center justify-center"
      >
        {thumb ? (
          <img
            src={thumb}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ) : (
          <Icon size={36} className="zita-ink-muted" />
        )}
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] zita-ink-muted mb-1">
          <Bell size={11} className="zita-orange-text" />
          {badge}
        </div>
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-serif text-lg sm:text-xl zita-ink leading-tight truncate">
            {op.title || 'Untitled listing'}
          </h3>
          {price && (
            <div className="shrink-0 font-semibold zita-orange-text text-lg">{price}</div>
          )}
        </div>
        {op.description && (
          <p className="mt-1 text-sm zita-ink-soft leading-snug line-clamp-2">{op.description}</p>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs zita-ink-muted">
          {op.location_text && (
            <span className="flex items-center gap-1">
              <MapPin size={12} /> {op.location_text}
              {typeof op.distance_miles === 'number' && (
                <span> · {op.distance_miles.toFixed(0)} mi</span>
              )}
            </span>
          )}
          <span className="flex items-center gap-1">
            <Clock size={12} /> Found {relTime(op.fetched_at)}
          </span>
          {op.source_name && <span>Source: <span className="zita-ink-soft">{op.source_name}</span></span>}
          {op.price_changed && typeof op.previous_price === 'number' && (
            <span className="zita-orange-text">Price changed (was ${op.previous_price.toLocaleString()})</span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {op.external_url && (
            <a
              href={op.external_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="alert-view-listing"
              className="zita-btn-primary text-xs !py-2 !px-4"
            >
              <ExternalLink size={12} /> View listing
            </a>
          )}
          <button
            type="button"
            onClick={() => onSave(op)}
            data-testid="alert-save-to-collection"
            className="zita-btn-secondary text-xs !py-2 !px-4 inline-flex items-center gap-1"
          >
            <Bookmark size={12} /> Save to collection
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const Alerts = () => {
  const [opps, setOpps] = useState([]);
  const [emptySummary, setEmptySummary] = useState(null);
  const [saving, setSaving] = useState(null); // single listing being saved
  const [savingAll, setSavingAll] = useState(false); // bulk-save modal open
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/alerts/feed');
      setOpps(data.opportunities || []);
      setEmptySummary(data.empty_summary || null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Group by opportunity_type for the summary strip.
  const groupSummary = () => {
    if (!opps.length) return null;
    const counts = {};
    for (const o of opps) counts[o.opportunity_type || 'item'] = (counts[o.opportunity_type || 'item'] || 0) + 1;
    const label = (k) =>
      k === 'lead' ? 'leads'
      : k === 'business' ? 'businesses'
      : k === 'candidate' ? 'candidates'
      : k === 'job' ? 'jobs'
      : k === 'delivery' ? 'delivery routes'
      : 'items';
    return Object.entries(counts).map(([k, v]) => `${v} ${label(k)}`).join(' · ');
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4 sm:px-8 py-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-4xl zita-ink">Alerts</h1>
          <p className="mt-1 zita-ink-soft">
            {opps.length ? (
              <>Fresh matches Ari found for you — {groupSummary()}.</>
            ) : (
              <>Real opportunities Ari finds for you will appear here.</>
            )}
          </p>
        </div>
        {opps.length > 0 && (
          <button
            type="button"
            onClick={() => setSavingAll(true)}
            data-testid="alerts-save-all"
            className="zita-btn-primary text-sm inline-flex items-center gap-1"
          >
            <Bookmark size={13} /> Save all {opps.length} matches
          </button>
        )}
      </header>

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="zita-card p-4 h-32 zita-fade-up opacity-40" />
          ))}
        </div>
      ) : opps.length === 0 ? (
        <div className="space-y-4">
          {emptySummary ? (
            <div className="zita-card p-5">
              <div className="text-[11px] uppercase tracking-[0.14em] zita-ink-muted mb-1">Scan complete</div>
              <div className="font-semibold zita-ink">{emptySummary.title}</div>
              <div className="text-sm zita-ink-soft mt-1">{emptySummary.body}</div>
              {emptySummary.scan_id && (
                <Link
                  to={`/results/${emptySummary.scan_id}`}
                  className="text-xs zita-blue-text hover:underline mt-2 inline-block"
                >
                  View scan details →
                </Link>
              )}
            </div>
          ) : (
            <EmptyState
              icon={Bell}
              title="No opportunities yet"
              body="Run a scan from Seek and Ari will bring the matches back here."
            />
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {opps.map((op) => (
            <OpportunityCard key={op.id} op={op} onSave={setSaving} />
          ))}
        </div>
      )}

      {saving && (
        <SaveToCollectionModal
          listing={saving}
          onClose={() => setSaving(null)}
          onSaved={() => setSaving(null)}
        />
      )}
      {savingAll && (
        <SaveToCollectionModal
          listings={opps}
          searchTerm="Alerts feed"
          onClose={() => setSavingAll(false)}
          onSaved={() => setSavingAll(false)}
        />
      )}
    </div>
  );
};

export default Alerts;
